import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { requestJSON } from '../core/http';
import { emitObservation } from '../core/observability';
import { LEGACY_STORAGE_KEYS, STORAGE_KEYS } from '../core/storage-keys';
import { createLogger } from '@shared/utils/logger';

const log = createLogger('SessionStore');

type SessionData = {
  session_id: string | number;
  table_id: string | number | null;
  table_code?: string | number;
  table_number?: string | number;
  anon_id: string;
  status: string;
  version?: number;
  expires_at: string | null;
};

type TableContext = {
  table_id: string;
  table_code?: string;
  source: 'qr' | 'kiosk' | 'manual' | 'unknown';
  locked_by_kiosk?: boolean;
  updated_at: string;
};

type ValidateRehydrateResponse = {
  success: boolean;
  valid: boolean;
  session_id?: string | number;
  table_id?: string | number;
  table_code?: string | number;
  table_number?: string | number;
  anon_id?: string;
  status?: string;
  version?: number;
};

function nowIso(): string {
  return new Date().toISOString();
}

function requireSessionStatus(status: unknown, source: string): string {
  const normalized = String(status ?? '').trim();
  if (!normalized) {
    throw new Error(`Session status is required from backend source: ${source}`);
  }
  return normalized;
}

function parseStoredTableContext(raw: string | null): TableContext | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TableContext>;
    const tableId = String(parsed.table_id || '').trim();
    if (!tableId) return null;
    const sourceRaw = String(parsed.source || 'unknown').trim().toLowerCase();
    const source: TableContext['source'] =
      sourceRaw === 'qr' || sourceRaw === 'kiosk' || sourceRaw === 'manual'
        ? sourceRaw
        : 'unknown';

    return {
      table_id: tableId,
      table_code: String(parsed.table_code || '').trim() || undefined,
      source,
      locked_by_kiosk: Boolean(parsed.locked_by_kiosk),
      updated_at: String(parsed.updated_at || nowIso()),
    };
  } catch {
    return null;
  }
}

function readUrlTableId(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return String(params.get('table_id') || '').trim();
  } catch {
    return '';
  }
}

function readUrlQrToken(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return String(params.get('qr_token') || '').trim();
  } catch {
    return '';
  }
}

function readUrlSessionId(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return String(params.get('session_id') || '').trim();
  } catch {
    return '';
  }
}

/** V3.6: Generate a stable anonymous client ID, persisted in localStorage.
 *  Format: anon_<timestamp_base36><random_base36>  (collision-resistant, not a UUID)
 *  Cleared when customer registers/logs in.
 */
const ANON_ID_KEY = 'enfa:anon_client_id';

function createAnonId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `anon_${ts}${rand}`;
}

function getOrCreateAnonId(): string {
  try {
    // eslint-disable-next-line no-restricted-properties -- anon ID is safe, not session state
    const existing = localStorage.getItem(ANON_ID_KEY);
    if (existing && existing.startsWith('anon_')) return existing;
    const newId = createAnonId();
    // eslint-disable-next-line no-restricted-properties
    localStorage.setItem(ANON_ID_KEY, newId);
    return newId;
  } catch {
    return createAnonId(); // private/incognito fallback (not persisted)
  }
}

function clearAnonId(): void {
  try {
    // eslint-disable-next-line no-restricted-properties
    localStorage.removeItem(ANON_ID_KEY);
  } catch { /* silent */ }
}

// Register on window for optional external helpers to override
if (typeof window !== 'undefined' && !('getOrCreateAnonymousClientId' in window)) {
  (window as any).getOrCreateAnonymousClientId = getOrCreateAnonId;
}

function getAnonymousClientId(): string | null {
  try {
    const helper = (window as any).getOrCreateAnonymousClientId;
    if (typeof helper === 'function') {
      const value = helper();
      const normalized = String(value || '').trim();
      return normalized || null;
    }
  } catch {
    // No-op, optional helper.
  }
  // V3.6 fallback: use local implementation directly
  return getOrCreateAnonId();
}

export const useSessionStore = defineStore('client-session', () => {
  const session = ref<SessionData | null>(null);
  const tableContext = ref<TableContext | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const bootstrapCompleted = ref(false);
  const sessionReady = ref(false);
  const sessionExpiredNotice = ref<string | null>(null); // UX-04: User-friendly expiration notice

  let rehydratingPromise: Promise<boolean> | null = null;

  // UX-04: Watch for session expiration and show user-friendly notice
  watch(
    [sessionReady],
    ([ready]) => {
      if (!ready && session.value?.session_id) {
        // Session went from true → false while we have a session_id (expiración)
        sessionExpiredNotice.value = 'Tu sesión ha expirado. Por favor, abre una nueva mesa.';
      } else {
        sessionExpiredNotice.value = null;
      }
    },
    { flush: 'post' },
  );

  const isAuthenticated = computed(() => Boolean(session.value?.session_id));
  const sessionId = computed(() => session.value?.session_id || null);

  const clearLegacyStorage = (): void => {
    for (const key of LEGACY_STORAGE_KEYS.tableContext) {
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        // Keep runtime resilient in private-mode/storage-restricted browsers.
      }
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Keep runtime resilient in private-mode/storage-restricted browsers.
      }
    }
  };

  const persistTableContext = (next: TableContext | null): void => {
    tableContext.value = next;
    try {
      if (!next) {
        window.sessionStorage.removeItem(STORAGE_KEYS.tableContext);
        return;
      }
      window.sessionStorage.setItem(STORAGE_KEYS.tableContext, JSON.stringify(next));
    } catch {
      // Ignore storage failures; source of truth is still backend + store state.
    }
  };

  const hydrateTableContext = (): TableContext | null => {
    const parsed = parseStoredTableContext(
      window.sessionStorage.getItem(STORAGE_KEYS.tableContext),
    );
    tableContext.value = parsed;
    return parsed;
  };

  const setTableContext = (
    tableId: string | number,
    source: TableContext['source'] = 'manual',
    tableCode?: string,
    lockedByKiosk = false,
  ): void => {
    const normalizedId = String(tableId || '').trim();
    if (!normalizedId) return;
    persistTableContext({
      table_id: normalizedId,
      table_code: String(tableCode || '').trim() || undefined,
      source,
      locked_by_kiosk: lockedByKiosk,
      updated_at: nowIso(),
    });
  };

  const sourceForRehydration = (): TableContext['source'] => {
    const currentSource = tableContext.value?.source;
    return currentSource === 'qr' || currentSource === 'kiosk' ? currentSource : 'manual';
  };

  const clearTableContext = (): void => {
    persistTableContext(null);
  };

  const persistRemoteTableContext = async (context: TableContext): Promise<void> => {
    try {
      await requestJSON('/api/sessions/table-context', {
        method: 'POST',
        body: {
          table_id: context.table_id,
          table_code: context.table_code || null,
          source: context.source,
        },
      });
    } catch (remoteError) {
      log.warn('Unable to persist table context remotely', remoteError);
    }
  };

  const initSession = async (tableId: string | number): Promise<string> => {
    const normalizedTableId = String(tableId || '').trim();
    if (!normalizedTableId) {
      throw new Error('Table context is required to start session');
    }

    loading.value = true;
    error.value = null;

    try {
      const anonId = getAnonymousClientId();
      const response = await requestJSON<{ data: SessionData }>('/api/v1/dining-sessions/open', {
        method: 'POST',
        body: {
          table_id: normalizedTableId,
          anon_id: anonId,
        },
      });
      const resolvedStatus = requireSessionStatus(response?.data?.status, '/api/v1/dining-sessions/open');

      session.value = {
        session_id: response.data.session_id,
        table_id: response.data.table_id ?? normalizedTableId,
        anon_id: response.data.anon_id || '',
        status: resolvedStatus,
        version: response.data.version,
        expires_at: response.data.expires_at || null,
      };

      setTableContext(response.data.table_id ?? normalizedTableId, 'manual', String(response.data.table_code || response.data.table_number || '').trim() || undefined);
      if (tableContext.value) {
        void persistRemoteTableContext(tableContext.value);
      }

      sessionReady.value = true;
      emitObservation('session_ready', {
        session_ready: true,
        session_id: String(session.value.session_id),
        source: 'init_session',
      });
      return String(session.value.session_id);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error al iniciar sesión';
      sessionReady.value = false;
      emitObservation('session_ready', {
        session_ready: false,
        source: 'init_session',
        reason: error.value,
      });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const resolveQrSession = async (qrToken: string): Promise<string> => {
    const normalizedToken = String(qrToken || '').trim();
    if (!normalizedToken) {
      throw new Error('QR token is required');
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await requestJSON<{ data: SessionData }>(
        `/api/sessions/qr/${encodeURIComponent(normalizedToken)}`,
        {
          method: 'POST',
        }
      );
      const resolvedStatus = requireSessionStatus(response?.data?.status, `/api/sessions/qr`);

      session.value = {
        session_id: response.data.session_id,
        table_id: response.data.table_id ?? null,
        anon_id: response.data.anon_id || '',
        status: resolvedStatus,
        version: response.data.version,
        expires_at: response.data.expires_at || null,
      };

      if (response.data.table_id) {
        setTableContext(response.data.table_id, 'qr', String((response as { table_context?: { code?: string } }).table_context?.code || '').trim() || undefined, false);
        if (tableContext.value) {
          void persistRemoteTableContext(tableContext.value);
        }
      }

      sessionReady.value = true;
      emitObservation('session_ready', {
        session_ready: true,
        session_id: String(session.value.session_id),
        source: 'resolve_qr',
      });
      return String(session.value.session_id);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error al procesar QR';
      sessionReady.value = false;
      emitObservation('session_ready', {
        session_ready: false,
        source: 'resolve_qr',
        reason: error.value,
      });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const clearSession = (clearAnon = false, isExpiry = false): void => {
    session.value = null;
    sessionReady.value = false;
    if (isExpiry) {
      sessionExpiredNotice.value = 'Tu sesión ha expirado. Por favor, abre una nueva mesa.';
    } else {
      sessionExpiredNotice.value = null;
    }
    if (clearAnon) clearAnonId();
  };

  const dismissExpiryNotice = (): void => {
    sessionExpiredNotice.value = null;
  };

  const refreshSession = async (): Promise<void> => {
    try {
      const response = await requestJSON<{ data: SessionData }>('/api/sessions/me');
      session.value = response.data;
      sessionReady.value = Boolean(response.data?.session_id);
    } catch (err) {
      log.warn('Failed to refresh session', err);
      clearSession();
    }
  };

  const validateAndRehydrate = async (): Promise<boolean> => {
    if (rehydratingPromise) {
      return rehydratingPromise;
    }

    rehydratingPromise = (async () => {
      try {
        const response = await requestJSON<ValidateRehydrateResponse>(
          '/api/sessions/validate-and-rehydrate',
          { method: 'POST' },
        );

        if (response.success && response.valid && response.session_id) {
          const normalizedTableId = String(response.table_id || '').trim() || null;
          const resolvedStatus = requireSessionStatus(
            response.status,
            '/api/sessions/validate-and-rehydrate'
          );
          session.value = {
            session_id: response.session_id,
            table_id: normalizedTableId,
            anon_id: String(response.anon_id || ''),
            status: resolvedStatus,
            version: response.version,
            expires_at: null,
          };

          if (normalizedTableId) {
            setTableContext(
              normalizedTableId,
              sourceForRehydration(),
              String(response.table_code || response.table_number || '').trim() || undefined,
            );
          }

          sessionReady.value = true;
          emitObservation('session_ready', {
            session_ready: true,
            session_id: String(response.session_id),
            source: 'rehydrate',
          });
          return true;
        }

        clearSession();
        emitObservation('session_ready', {
          session_ready: false,
          source: 'rehydrate',
          reason: 'invalid_or_missing_session',
        });
        return false;
      } catch (err) {
        log.warn('Failed to rehydrate session', err);
        clearSession();
        emitObservation('session_ready', {
          session_ready: false,
          source: 'rehydrate',
          reason: err instanceof Error ? err.message : 'unknown_error',
        });
        return false;
      } finally {
        rehydratingPromise = null;
      }
    })();

    return rehydratingPromise;
  };

  const rehydrateSessionById = async (sessionId: string | number): Promise<boolean> => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId) return false;

    try {
      const response = await requestJSON<ValidateRehydrateResponse>(
        `/api/v1/dining-sessions/${encodeURIComponent(normalizedSessionId)}/rehydrate`,
      );

      if (response.success && response.valid && response.session_id) {
        const normalizedTableId = String(response.table_id || '').trim() || null;
        const source = `/api/v1/dining-sessions/${normalizedSessionId}/rehydrate`;
        session.value = {
          session_id: response.session_id,
          table_id: normalizedTableId,
          anon_id: String(response.anon_id || ''),
          status: requireSessionStatus(response.status, source),
          version: response.version,
          expires_at: null,
        };

        if (normalizedTableId) {
          setTableContext(
            normalizedTableId,
            sourceForRehydration(),
            String(response.table_code || response.table_number || '').trim() || undefined,
          );
        }

        sessionReady.value = true;
        emitObservation('session_ready', {
          session_ready: true,
          session_id: String(response.session_id),
          source: 'rehydrate_by_id',
        });
        return true;
      }
    } catch (err) {
      log.warn('Failed to rehydrate session by id', err);
    }

    return false;
  };

  const bootstrapSessionContext = async (): Promise<boolean> => {
    loading.value = true;
    error.value = null;
    clearLegacyStorage();
    hydrateTableContext();

    // Keep server-provided cookie session only when status is explicit.
    const appSessionId = String(window.APP_SESSION?.session_id || '').trim();
    if (appSessionId) {
      const rawAppSessionStatus = (window.APP_SESSION as { status?: unknown } | undefined)?.status;
      if (!String(rawAppSessionStatus ?? '').trim()) {
        log.warn('Skipping APP_SESSION seed because backend status is missing');
      } else {
        session.value = {
          session_id: appSessionId,
          table_id: null,
          anon_id: '',
          status: requireSessionStatus(rawAppSessionStatus, 'APP_SESSION'),
          version: (window.APP_SESSION as any)?.version,
          expires_at: null,
        };
      }
    }

    try {
      // #885: An explicit QR scan (via cookie or url) takes priority over previous sessions
      const qrCode = readUrlQrToken();
      const matchCookie = window.document.cookie.match(/(?:(?:^|.*;\s*)pending_qr_token\s*=\s*([^;]*).*$)|^.*$/);
      const pendingQrToken = matchCookie ? matchCookie[1] : null;

      const tokenToResolve = pendingQrToken || qrCode;

      if (tokenToResolve) {
        if (pendingQrToken) {
          // Clear cookie immediately
          window.document.cookie = "pending_qr_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
        }
        try {
          await resolveQrSession(tokenToResolve);
          bootstrapCompleted.value = true;
          return true;
        } catch (qrErr) {
          log.warn('Failed to resolve QR token during bootstrap, falling back to session rehydration', qrErr);
        }
      }

      const rehydrated = await validateAndRehydrate();
      if (rehydrated) {
        // Successful recovery must clear the failed QR's transient error.
        error.value = null;
        bootstrapCompleted.value = true;
        return true;
      }

      const querySessionId = readUrlSessionId();
      if (querySessionId && (await rehydrateSessionById(querySessionId))) {
        bootstrapCompleted.value = true;
        return true;
      }

      const persistedTableId = String(tableContext.value?.table_id || '').trim();
      const queryTableId = readUrlTableId();
      const tableIdToOpen = persistedTableId || queryTableId;

      if (tableIdToOpen) {
        await initSession(tableIdToOpen);
        bootstrapCompleted.value = true;
        return true;
      }

      sessionReady.value = false;
      emitObservation('session_ready', {
        session_ready: false,
        source: 'bootstrap',
        reason: 'missing_table_context',
      });
      return false;
    } catch (err) {
      log.warn('Failed to bootstrap session context', err);
      return false;
    } finally {
      bootstrapCompleted.value = true;
      loading.value = false;
    }
  };

  return {
    session,
    tableContext,
    loading,
    error,
    sessionReady,
    sessionExpiredNotice, // UX-04: Expiration notice for users
    bootstrapCompleted,
    isAuthenticated,
    sessionId,
    initSession,
    resolveQrSession,
    clearSession,
    refreshSession,
    validateAndRehydrate,
    rehydrateSessionById,
    bootstrapSessionContext,
    setTableContext,
    clearTableContext,
    persistRemoteTableContext,
    dismissExpiryNotice,
  };
});
